define('models/Chat',[
    'lib/lodash4',
    'lib/knockout',
    'models/Wrapup',
    'enums/participantStatus',
    'services/authService',
    'services/liveDataService',
    'services/loggingService',
    'util/ObservableModel'
], function (_, ko, Wrapup, participantStatus, authService, liveDataService, LOG, ObservableModel) {
    'use strict';

    var MACHINE_PARTICIPANTS = ['acd', 'ivr', 'dialer.system'];

    var ChatParticipant = ObservableModel.extend({
        defaults: {
            state: null,
            held: false,
            roomId: null
        }
    }, function () {
    });

    var Chat = ObservableModel.extend({
        defaults: {
            participants: [],
            connectedDate: null,
            wrapup: null
        },
        modelProperties: {
            wrapup: Wrapup,
            participants: ObservableModel.extend({
                defaults: {
                    userUri: null,
                    userId: null,
                    purpose: null,
                    chats: null,
                    name: null,
                    attributes: null
                },
                modelProperties: {
                    chats: ChatParticipant
                }
            }, function () {
                var self = this;

                // At some point in the future, it will be possible for a conversation to have multiple chats.
                // For now, there will just be one.  This provides a wrapper around getting the current chat
                // so that we can inspect if it's being held and it's status.
                self.currentChat = ko.computed(function () {
                    if (self.chats().length > 0) {
                        return self.chats()[0];
                    }
                    return null;
                });

                self.status = ko.computed({
                    read: function () {
                        if (self.currentChat()) {
                            return self.currentChat().state().toUpperCase();
                        }
                        return null;
                    },
                    write: function (value) {
                        if (self.currentChat()) {
                            self.currentChat().state(value.toUpperCase());
                        }
                    }
                });

                self.held = ko.computed({
                    read: function () {
                        if (self.currentChat()) {
                            return self.currentChat().held();
                        }
                        return null;
                    },
                    write: function (value) {
                        if (self.currentChat()) {
                            self.currentChat().held(value);
                        }
                    }
                });

                self.roomId = ko.computed({
                    read: function () {
                        if (self.currentChat()) {
                            return self.currentChat().roomId();
                        }
                        return null;
                    },
                    write: function (value) {
                        if (self.currentChat()) {
                            self.currentChat().roomId(value);
                        }
                    }
                });

                ko.computed(function () {
                    if (self.userUri() && !self.userId()) {
                        var index = self.userUri().lastIndexOf('/');
                        self.userId(self.userUri().substring(index+1));
                    }
                });
            })
        }
    }, function () {
        var self = this;
        var ownerId = authService.principalId();

        self.interactionType = ko.observable('chat');

        self.channel = ko.observable();

        self.chatHistory = ko.observableArray([]);
        /**
         * Get the participant object that shares the same user ID as the currently
         * authenticated user.   The list is searched in reverse order as newer participants
         * are added to the end, and we need to be sure to get the most recent instance of the
         * authenticated user.
         * @return {Object} an object whose attributes represent a participant.
         */
        self.participantForCurrentUser = ko.computed(function () {
            return _.findLast(self.participants(), function (participant) {
                return (participant.userId() === ownerId);
            });
        });

        self.queueId = ko.computed(function () {
            if(self.participantForCurrentUser() && self.participantForCurrentUser().queueId){
                return self.participantForCurrentUser().queueId();
            }
            return null;
        });

        self.roomForCurrentUser = ko.computed(function () {
            if (self.participantForCurrentUser()) {
                return self.participantForCurrentUser().roomId();
            }
            return null;
        });

        var isCurrentUserOrMachineParticipant = function (participant) {
            return  participant.userId() === ownerId || isMachineParticipant(participant);
        };

        var isMachineParticipant = function (participant) {
            if (!participant.purpose) {
                return false;
            }

            return _.indexOf(MACHINE_PARTICIPANTS, participant.purpose()) > -1;
        };

        self.otherParticipants = ko.computed(function () {
            return _.reject(self.participants(), isCurrentUserOrMachineParticipant);
        });

        self.customerParticipant = ko.computed(function () {
            return _.find(self.participants(), function (participant) {
                return participant.purpose && participant.purpose() === 'customer';
            });
        });

        self.isHeld = ko.computed(function () {
            if (!self.participantForCurrentUser()) {
                return false;
            }

            return self.participantForCurrentUser().held &&
                   self.participantForCurrentUser().held();
        });

        self.isAlerting = ko.computed(function () {
            if (!self.participantForCurrentUser()) {
                return false;
            }

            var status = self.participantForCurrentUser().status();
            var isAlerting = (status === participantStatus.ALERTING);
            if (isAlerting) {
                LOG.debug("Alerting User Participant.");
            }
            return isAlerting;
        });

        self.isConnected = ko.computed(function () {
            if (!self.participantForCurrentUser()) {
                return false;
            }

            var status = self.participantForCurrentUser().status();
            var isConnected = (status === participantStatus.CONNECTED);
            if (isConnected) {
                LOG.debug("User Participant Connected");
            }
            return isConnected;
        });

        self.isDisconnected = ko.computed(function () {
            if (!self.participantForCurrentUser()) {
                return false;
            }

            var status = self.participantForCurrentUser().status();
            return (status === participantStatus.DISCONNECTED);
        });

        self.isContacting = ko.computed(function () {
            if (!self.participantForCurrentUser()) {
                return false;
            }

            var status = self.participantForCurrentUser().status();
            var isContacting = (status === participantStatus.CONTACTING);
            if (isContacting) {
                LOG.debug("User Participant Contacting");
            }
            return isContacting;
        });

        self.state = ko.computed(function () {
            if (self.isAlerting()) {
                return 'alerting';
            }
            return 'connected';
        });

        /**
         * If wrapup code is present then ACW has already been completed for this chat
         * @type {*}
         */
        self.isAfterCallWorkCompleted = ko.computed(function(){
            if (!self.participantForCurrentUser()) {
                return false;
            }
            var wrapup = ko.unwrap(self.participantForCurrentUser().wrapup);
            if (wrapup && wrapup.code()){
                return true;
            }
            return false;
        });

        /**
         * This function indicates whether ACW for chat is needed.
         * TODO: For now it simply checks if ACW is already completed
         * Down the road it may depend on the mandatory flag and queue settings.
         */
        self.isAfterCallWorkNeeded = ko.computed(function(){
            return !self.isAfterCallWorkCompleted();
        });

        /**
         * If the chat is currently doing after call work, this will be true;
         */
        self.isInAfterCallWork = ko.observable(false);

        self.active = ko.computed(function () {
            if (!self.participantForCurrentUser()) {
                return false;
            }
            return !self.isDisconnected() || self.isAfterCallWorkNeeded();
        });
    });

    liveDataService.registerTopic('chats', Chat);

    return Chat;
});
